import { initializeApp } from 'firebase/app';
import {
  browserLocalPersistence,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  connectAuthEmulator,
  indexedDBLocalPersistence,
  initializeAuth,
} from 'firebase/auth';
import { FIREBASE, IS_DEV } from './env';

/**
 * Firebase configuration and initialization
 * Handles both development (emulator) and production environments
 */

// Firebase config - these are safe to expose in frontend
const firebaseConfig = {
  apiKey: FIREBASE.apiKey,
  // CRITICAL: In dev mode, don't set authDomain to let emulator work properly
  // Firebase SDK will try to validate against authDomain, and benenota-dev.firebaseapp.com
  // redirects to production, causing emulator to be bypassed
  authDomain: IS_DEV ? undefined : FIREBASE.authDomain,
  projectId: FIREBASE.projectId,
  storageBucket: FIREBASE.storageBucket,
  messagingSenderId: FIREBASE.messagingSenderId,
  appId: FIREBASE.appId,
  // NO `measurementId`. Supplying it is what lets the Firebase SDK stand up
  // Google Analytics; see the analytics note at the bottom of this file.
};

// CRITICAL: In development, clear ALL persistence BEFORE initializing Auth
// This prevents loading cached production tokens
// DISABLED: This was causing the user to be logged out on every refresh
/*
if (IS_DEV) {
  console.log('🔥 Firebase Dev Mode - clearing all cached auth state...');

  // 1. Clear localStorage
  const firebaseKeys = Object.keys(localStorage).filter((key) => key.includes('firebase'));
  firebaseKeys.forEach((key) => {
    localStorage.removeItem(key);
  });
  console.log(`🗑️ Cleared ${firebaseKeys.length} localStorage keys`);

  // 2. Clear sessionStorage
  const sessionKeys = Object.keys(sessionStorage).filter((key) => key.includes('firebase'));
  sessionKeys.forEach((key) => {
    sessionStorage.removeItem(key);
  });
  console.log(`🗑️ Cleared ${sessionKeys.length} sessionStorage keys`);

  // 3. Clear IndexedDB (asynchronously, but start now)
  if ('indexedDB' in window) {
    indexedDB
      .databases()
      .then((dbs) => {
        dbs.forEach((db) => {
          if (db.name && db.name.includes('firebase')) {
            console.log(`🗑️ Deleting IndexedDB: ${db.name}`);
            indexedDB.deleteDatabase(db.name);
          }
        });
      })
      .catch(() => {
        // Fallback: delete common Firebase IndexedDB names
        [
          'firebaseLocalStorageDb',
          'firebase-heartbeat-database',
          'firebase-heartbeat-store',
        ].forEach((dbName) => {
          indexedDB.deleteDatabase(dbName);
        });
      });
  }

  console.log('🔥 Firebase Config:', {
    projectId: firebaseConfig.projectId,
    mode: import.meta.env.MODE,
  });
}
*/

// Initialize Firebase
const app = initializeApp(firebaseConfig);

/**
 * The persistence order below is what keeps sibling tabs signed in. Do not
 * swap it back to `getAuth()` + `setPersistence()`.
 *
 * That pair (used until 2026-09-30) moved the stored session on EVERY page
 * load: `getAuth()` prefers IndexedDB, so it copied the session out of
 * localStorage and deleted it there; the later `setPersistence(local)` deleted
 * it from IndexedDB and only then wrote it back to localStorage. When a
 * workspace switch reloads every other tab at once (App.tsx), a tab reading
 * between those steps finds both stores empty and paints the login page over a
 * live session. Firebase then deletes the key from the other stores too, which
 * can wipe a sibling's freshly written copy. Reproduced in a real browser: 82
 * of 480 simultaneous tab loads came up signed out with the old pair, 0 of 480
 * with this.
 *
 * localStorage FIRST because that is where the old pair left every existing
 * session, so upgrading reads it in place with no migration. IndexedDB is
 * listed so a session found there is moved to localStorage once; session
 * storage is listed so a session found there is still read (Firebase keeps it
 * in place rather than moving it). Neither is ever dropped.
 *
 * Consequence to know about: a sign-out in one tab now signs out EVERY tab at
 * once. Firebase listens for changes on the store Auth was built on, and the
 * old pair built it on IndexedDB while the session lived in localStorage, so
 * that sync was silently dead (a sibling only found out at its next reload,
 * often a workspace switch). Anything that signs out on a per-tab judgement
 * must therefore judge across tabs. The idle auto-logout does
 * (hooks/ui/useIdleTimeout.ts), so an untouched background tab cannot sign
 * out the tab in use. api.service.ts's 401 refresh signs out only when
 * Firebase says the refresh token itself is gone (revoked, expired, user
 * disabled or deleted), which is true in every tab, or when this tab has no
 * Firebase user to refresh at all (the legacy native bridge, or already
 * signed out). Never on a network failure or because the replay failed.
 */
const auth = initializeAuth(app, {
  persistence: [browserLocalPersistence, indexedDBLocalPersistence, browserSessionPersistence],
  // getAuth() supplied this implicitly; the Google/Microsoft/Apple popups need it.
  popupRedirectResolver: browserPopupRedirectResolver,
});

// Connect to emulator in development mode - MUST be done before any auth operations
if (IS_DEV) {
  try {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    console.debug('✅ Connected to Firebase Auth Emulator at http://127.0.0.1:9099');
    console.debug('✅ Development mode ready - emulator connected, production state cleared');
  } catch (error) {
    console.error('❌ Failed to connect to Auth Emulator:', error);
  }
}

/**
 * Firebase Analytics is deliberately NOT initialised, and `measurementId` is
 * deliberately absent from the config above.
 *
 * It used to be constructed unconditionally in production, and it was a real
 * egress leak on two counts (both observed in prod on 2026-08-23, on the
 * `G-2C15MFLK7S` stream):
 *
 *  - It ignored cookie consent. `utils/cookieConsent.ts` gates PostHog and
 *    Langfuse; nothing here consulted it. With consent stored as
 *    `analytics: false`, gtag.js still loaded and still sent
 *    `en=page_view` with `dl=https://<host>/artifacts` — an authenticated
 *    in-app URL.
 *  - It sent that to GOOGLE_ANALYTICS, which `config/egress-policy.yaml`
 *    scopes to "Marketing-site measurement plus the app's pre-authentication
 *    pages only" and marks `baa_covered: false`. Firing on authenticated
 *    routes is outside that declared scope, and this app is inside the HIPAA
 *    boundary.
 *
 * `utils/ga4.ts` is the ONLY sanctioned GA4 path: an exact-match allowlist of
 * credential-entry routes, a permanent seal once a session authenticates, a
 * consent check, and its own dedicated stream. It was never the leak.
 *
 * Removing this cost nothing: no module imported the `analytics` binding and
 * the app never called `logEvent`, so the SDK's automatic `page_view` was the
 * entire behaviour. If Firebase Analytics is ever genuinely wanted, it needs a
 * consent gate, a PHI gate, and an egress-policy destination first — do not
 * restore it by adding `measurementId` back.
 */

export { auth };
